package ai.jessie.web.theme

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

fun calendarHref(organisationId: String) = "/organisations/$organisationId/calendar"

/** Navigation only; every destination rechecks permissions on the server. */
fun homeHref(organisationId: String, role: String) =
    if (role == "ACCOUNTANT") "/organisations/$organisationId/documents"
    else calendarHref(organisationId)

fun homeSideItems(organisationId: String): List<SideItem> =
    listOf(
        SideItem("Kalender", calendarHref(organisationId), "calendar-alt"),
        SideItem("Meine Stunden", "/organisations/$organisationId/work", "clock"),
        SideItem("Organisation", "/organisations/$organisationId", "building"),
    )

/**
 * Settings side navigation (`19`/`20`/`18-Einstellungen*.png`): only routes that exist. Without an
 * organisation only the cross-organisation profile remains.
 */
fun settingsSideGroups(organisationId: String?, organisationName: String?): List<SideGroup> {
    val profile =
        SideGroup("Übergreifend", listOf(SideItem("Profil", profileHref(organisationId), "user")))
    if (organisationId == null) return listOf(profile)
    return listOf(
        profile,
        SideGroup(
            organisationName?.let { "Organisation $it" } ?: "Organisation",
            listOf(
                SideItem("Allgemein", "/organisations/$organisationId/settings", "building"),
                SideItem(
                    "Mitarbeiter:innen",
                    "/organisations/$organisationId/members",
                    "user-circle",
                ),
            ),
        ),
    )
}

/** The profile keeps the selected organisation (UC-026 `?organisationId=`). */
fun profileHref(organisationId: String?) =
    organisationId?.let { "/profile?organisationId=$it" } ?: "/profile"

@Composable
fun appFrame(
    organisationId: String?,
    current: String,
    title: String,
    kicker: String? = null,
    subtitle: String? = null,
    kpis: List<Kpi> = emptyList(),
    authenticationRequired: Boolean = false,
    side: List<SideItem> = emptyList(),
    sideGroups: List<SideGroup> = emptyList(),
    crumb: (@Composable () -> Unit)? = null,
    badge: (@Composable () -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null,
    // False when the page moves focus between its views itself; the title is focused once.
    refocusTitle: Boolean = true,
    content: @Composable () -> Unit,
) {
    val home = "/organisations/$organisationId"
    val landing = "$home?home=true"
    topNav(
        // The global profile has no organisation, so no organisation routes are offered.
        items =
            if (organisationId == null) emptyList()
            else
                listOf(
                    NavItem("Home", landing),
                    NavItem("Projekte", "$home/projects"),
                    NavItem("Firmen und Personen", "$home/customers"),
                    // The invoice list until UC-016 Finanzen exists (decision 18 October 2026).
                    NavItem("Finanzen", "$home/invoices"),
                    // The UC-017 inbox (decision 18 October 2026).
                    NavItem("Dokumente", "$home/documents"),
                    NavItem("Einstellungen", "$home/settings"),
                ),
        current =
            when {
                side.any { it.href == current } -> landing
                // Captioned side groups belong to the settings area.
                sideGroups.isNotEmpty() -> "$home/settings"
                else -> current
            },
        // The existing session boundary exposes status only, not a member name.
        initials = null,
    )
    page(lang = "de") {
        pageHeader(title, kicker, subtitle, kpis, crumb = crumb, badge = badge, actions = actions)
        when {
            side.isNotEmpty() ->
                columns(side = { sideNav(side, current, label = "Home") }) { content() }
            sideGroups.isNotEmpty() ->
                columns(side = { sideNav(sideGroups, current, label = title) }) { content() }
            else -> content()
        }
        P { A(href = "/support") { Text("Hilfe und Kontakt") } }
        if (!authenticationRequired) {
            Form(action = "/api/v1/authentication/logout", attrs = { attr("method", "post") }) {
                themedButton("Abmelden", {}, submit = true)
            }
            P(attrs = SmallStyle.toAttrs()) {
                Text(
                    "Die Abmeldung beendet auch deine Sitzung beim Anmeldedienst in diesem Browser."
                )
            }
        }
    }
    // A problem rendered without a request (e.g. an invalid link) takes focus instead of the title.
    LaunchedEffect(if (refocusTitle) title else Unit) {
        if (!AppGlobals.isExporting)
            (document.getElementById("onboarding-error") ?: document.getElementById("page-title"))
                .let { it as? HTMLElement }
                ?.focus()
    }
}

/**
 * Frame for pages without an organisation (landing, registration, onboarding, selection, support):
 * the top bar without entries, then one centred card with the title, content, help and logout.
 */
@Composable
fun entryFrame(
    title: String,
    authenticationRequired: Boolean = false,
    content: @Composable () -> Unit,
) {
    topNav(items = emptyList(), current = null, initials = null)
    page(lang = "de") {
        Div(attrs = listOf(CardStyle, TextCardStyle, EntryCardStyle).toAttrs()) {
            Div(attrs = CardBodyStyle.toAttrs()) {
                pageHeader(title)
                content()
            }
            Div(attrs = EntryFootStyle.toAttrs()) {
                P { A(href = "/support") { Text("Hilfe und Kontakt") } }
                if (!authenticationRequired) {
                    Form(
                        action = "/api/v1/authentication/logout",
                        attrs = { attr("method", "post") },
                    ) {
                        themedButton("Abmelden", {}, submit = true)
                    }
                    P(attrs = SmallStyle.toAttrs()) {
                        Text(
                            "Die Abmeldung beendet auch deine Sitzung beim Anmeldedienst in " +
                                "diesem Browser."
                        )
                    }
                }
            }
        }
    }
    LaunchedEffect(title) {
        if (!AppGlobals.isExporting)
            (document.getElementById("page-title") as? HTMLElement)?.focus()
    }
}
